import { useDispatch, useSelector } from "react-redux";
import { setShowTaskSidebar } from "../../actions/taskSidebar";
import { notifyIcon, number, route } from "../../config";
import { getUTCDate, keepUtcDate } from "../../helper/dateHelper";
import { appendFieldsFromObject, createFormData } from "../../helper/formDataHelper";
import { deleteContentAttachment } from "../../shared/services/contentManger.services";
import { editFormSubmission, submitForm } from "../../shared/services/workflow.services";
import { getNotification } from "../../utils/common";
import { completeAndForward, completeAndForwardRuleBased, newTaskAttachments } from "../Tasks/tasks.service";
import { FORM_TABS, FORM_TABS_KEYS } from "./FormsConstant";
import { collectFormElements, isShowIfVisible } from "./utils/showIf.utils";

/**
 * Custom hook for handling form submissions with file uploads
 * Provides utilities for converting files, extracting form files, and submitting forms
 * @returns {Object} Object containing form submission handlers and utilities
 */
export const useFormSubmission = (formTab = FORM_TABS.SUBSECTIONS_OBJ) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { positionId } = useSelector((state) => state.userPosition);
  const { allAttachments } = useSelector((state) => state.attachments);

  /**
   * Convert Kendo Upload File object to regular File object
   * @param {Object} kendoFile - Kendo Upload file object
   * @returns {File} - Regular File object
   */
  const convertKendoFileToFile = (kendoFile) => {
    // Kendo Upload stores the actual File/Blob via getRawFile() function
    if (kendoFile && typeof kendoFile.getRawFile === "function") {
      return kendoFile.getRawFile();
    }

    // Fallback: check for rawFile property (in case it exists)
    if (kendoFile && kendoFile.rawFile) {
      return kendoFile.rawFile;
    }

    // If it's already a File/Blob object, return as is
    if (kendoFile instanceof File || kendoFile instanceof Blob) {
      return kendoFile;
    }

    console.warn("Unable to extract File/Blob from Kendo Upload object:", kendoFile);
    return null;
  };

  /**
   * Converts a blob URL to a blob object by fetching it
   * @param {string} blobURL - The blob URL to fetch
   * @returns {Promise<Blob>} The blob object
   * @author Sejal
   */
  const getBlobFromURL = async (blobURL) => {
    const response = await fetch(blobURL);
    const blob = await response.blob();
    return blob;
  };

  /**
   * Converts a blob to a File object with specified filename
   * @param {Blob} blob - The blob to convert
   * @param {string} fileName - The filename for the resulting File
   * @returns {File} The File object created from the blob
   * @author Sejal
   */
  const blobToFile = (blob, fileName) => {
    // Create a File from the Blob
    const file = new File([blob], fileName, {
      type: blob.type,
      lastModified: Date.now(),
    });
    return file;
  };

  /**
   * Convert blob URL to File object
   * @param {string} blobURL - The blob URL to convert
   * @param {string} fileName - The filename for the resulting File
   * @returns {Promise<File>} The File object created from the blob URL
   */
  const convertBlobURLToFile = async (blobURL, fileName) => {
    try {
      // Fetch the Blob from the Blob URL
      const blob = await getBlobFromURL(blobURL);

      // Convert the Blob to a File
      const file = blobToFile(blob, fileName);

      return file;
    } catch (error) {
      console.error("Error converting Blob URL to File:", error);
      return null;
    }
  };

  /**
   * Extract files from form responses for file-upload elements
   * @param {Object} formResponses - Form responses containing files
   * @param {Object} formContent - Form structure to identify file-upload elements
   * @param {Object} formTab - Form tab data
   * @returns {Array} - Array of File objects ready for upload
   */
  const extractFilesFromFormResponses = (formResponses, formContent, formTab) => {
    const files = [],
      filesWithIds = [];

    if (!formContent?.[formTab?.hostSection]) return { files, filesWithIds };

    formContent?.[formTab?.hostSection]?.forEach((subsection) => {
      if (subsection.elements) {
        subsection.elements.forEach((element) => {
          // Handle regular file-upload elements
          if (element.type === "file-upload" && formResponses[element.id]) {
            const elementFiles = formResponses[element.id];
            if (Array.isArray(elementFiles)) {
              elementFiles.forEach((file) => {
                if (file?.rawFile instanceof File) {
                  // New modal-based device file
                  files.push(file.rawFile);
                  filesWithIds.push({ id: element.id, file: file.rawFile });
                } else {
                  // Legacy Kendo file fallback
                  const convertedFile = convertKendoFileToFile(file);
                  if (convertedFile) {
                    files.push(convertedFile);
                    filesWithIds.push({ id: element.id, file: convertedFile });
                  }
                }
              });
            }
          }

          // Handle file-upload elements inside grid containers
          if (element.type === "row-container" && element.gridElements) {
            element.gridElements.forEach((row) => {
              row.forEach((gridElement) => {
                if (gridElement && gridElement.type === "file-upload" && formResponses[gridElement.id]) {
                  const elementFiles = formResponses[gridElement.id];
                  if (Array.isArray(elementFiles)) {
                    elementFiles.forEach((file) => {
                      if (file?.rawFile instanceof File) {
                        files.push(file.rawFile);
                        filesWithIds.push({ id: gridElement.id, file: file.rawFile });
                      } else {
                        const convertedFile = convertKendoFileToFile(file);
                        if (convertedFile) {
                          files.push(convertedFile);
                          filesWithIds.push({ id: gridElement.id, file: convertedFile });
                        }
                      }
                    });
                  }
                }
              });
            });
          }

          // Handle fileUpload columns inside table elements
          if (element.type === "table" && formResponses[element.id]) {
            const tableRows = formResponses[element.id];
            element.properties?.columns?.forEach((col, colIndex) => {
              if (col.type === "fileUpload") {
                tableRows.forEach((row, rowIndex) => {
                  const cellValue = row[`col_${colIndex}`];
                  if (cellValue?.rawFile instanceof File) {
                    const compositeKey = `${element.id}__${rowIndex}__${colIndex}`;
                    files.push(cellValue.rawFile);
                    filesWithIds.push({ id: compositeKey, file: cellValue.rawFile });
                  }
                });
              }
            });
          }
        });
      }
    });

    return { files, filesWithIds };
  };

  /**
   * Collects attachmentKey (link) for every CMS file across all file-upload
   * elements in the form responses. Files marked pendingDelete are excluded.
   * Returns a flat array of attachment-key strings.
   */
  const extractCmsFileLinks = (formResponses, formContent, formTab) => {
    const links = [];
    if (!formContent?.[formTab?.hostSection]) return links;

    const collectFromList = (fileList, elementId) => {
      if (!Array.isArray(fileList)) return;
      fileList.forEach((file) => {
        if (file?.source === "cms" && !file?.pendingDelete && file?.attachmentKey) {
          links.push({ filename: file.name, key: file.attachmentKey, elementId: elementId, size: file.size });
        }
      });
    };

    formContent?.[formTab?.hostSection]?.forEach((subsection) => {
      if (!subsection.elements) return;
      subsection.elements.forEach((element) => {
        if (element.type === "file-upload" && formResponses[element.id]) {
          collectFromList(formResponses[element.id], element.id);
        }

        if (element.type === "row-container" && element.gridElements) {
          element.gridElements.forEach((row) => {
            row.forEach((gridElement) => {
              if (gridElement?.type === "file-upload" && formResponses[gridElement.id]) {
                collectFromList(formResponses[gridElement.id], gridElement.id);
              }
            });
          });
        }

        if (element.type === "table" && formResponses[element.id]) {
          const tableRows = formResponses[element.id];
          element.properties?.columns?.forEach((col, colIndex) => {
            if (col.type === "fileUpload") {
              tableRows.forEach((row) => {
                const cell = row[`col_${colIndex}`];
                if (cell?.source === "cms" && !cell?.pendingDelete && cell?.attachmentKey) {
                  links.push({ filename: cell.name, key: cell.attachmentKey });
                }
              });
            }
          });
        }
      });
    });

    return links;
  };

  /**
   * Process files for attachment upload
   * @param {Array} uploadFiles - Array of File objects to process
   * @param {Function} getAttachmentObject - Function to create attachment objects
   * @returns {Promise<Array>} Array of processed file attachments
   */
  const processFilesForUpload = async (uploadFiles, getAttachmentObject) => {
    const filesForUI = [];

    for (let i = number.ZERO; i < uploadFiles.length; i++) {
      // Optional: Add file size validation here
      // if (!isValidSize(uploadFiles[i].size, null)) {
      //   continue;
      // }

      const fileBlob = convertKendoFileToFile(uploadFiles[i]);
      const imageUrl = fileBlob ? URL?.createObjectURL(fileBlob) : null;
      const attachmentObject = getAttachmentObject(fileBlob || uploadFiles[i], imageUrl, user);

      filesForUI.push(attachmentObject);
    }

    // Convert blob URLs to File objects for upload
    const fileAttachments = await Promise.all(
      filesForUI.map((attachment) => {
        const { AttachmentLink, AttachmentName } = attachment;
        return convertBlobURLToFile(AttachmentLink, AttachmentName);
      })
    );

    return fileAttachments.filter((file) => file !== null); // Filter out failed conversions
  };

  /**
   * Upload files as task attachments
   * @param {string} taskId - The task ID to attach files to
   * @param {Array} fileAttachments - Array of File objects to upload
   * @returns {Promise<Object>} The attachment upload response
   */
  const uploadTaskAttachments = async (taskId, fileAttachments) => {
    const attachmentPayload = {
      entityName: "Tasks",
      entityId: taskId,
      files: fileAttachments,
      copyAttachments: JSON.stringify([]), // No copy attachments for form uploads
    };

    const fileFormData = createFormData();
    appendFieldsFromObject(fileFormData, attachmentPayload);

    // Upload files using existing newTaskAttachments service
    const attachmentResponse = await dispatch(newTaskAttachments(fileFormData));
    return attachmentResponse;
  };

  /**
   * Apply default values to form elements that don't have user responses
   * @param {Object} element - Form element
   * @param {boolean} hasUserResponse - Whether user provided a response
   * @returns {Object} Element with default value applied if needed
   */
  const applyDefaultValues = (element, hasUserResponse) => {
    // Skip if user provided a response or no default value exists
    if (hasUserResponse || !element.properties) return element;

    const { type, properties } = element;
    let defaultVal = null;

    // Get default value based on element type
    switch (type) {
      case "date":
        if (properties.defaultValue === "Current Date") {
          defaultVal = keepUtcDate(new Date());
        } else if (properties.defaultValue) {
          defaultVal = properties.defaultValue;
        }
        break;

      case "dropdown":
        if (properties.default && properties.default !== "" && properties.default !== null && properties.default !== undefined) {
          defaultVal = properties.default;
        }
        break;

      case "text-label":
      case "text":
        if (properties.defaultValue && properties.defaultValue !== "" && properties.defaultValue !== null && properties.defaultValue !== undefined) {
          defaultVal = properties.defaultValue;
        }
        break;

      case "number":
        if (properties.defaultValue !== null && properties.defaultValue !== undefined && properties.defaultValue !== "") {
          defaultVal = properties.defaultValue;
        }
        break;

      default:
        // For other types, check for general defaultValue
        if (properties.defaultValue !== null && properties.defaultValue !== undefined && properties.defaultValue !== "") {
          defaultVal = properties.defaultValue;
        }
        break;
    }

    // Apply default value if found
    if (defaultVal !== null && defaultVal !== undefined) {
      return {
        ...element,
        value: defaultVal,
      };
    }

    return element;
  };

  /**
   * Helper function to update element with form response
   * @param {Object} element - The element to update
   * @param {Object} formResponses - User form responses
   * @param {Function} getCalculationValue - Function to get calculated values
   * @returns {Object} Updated element
   */
  const updateElementWithResponse = (element, formResponses, getCalculationValue) => {
    const hasUserResponse = Object.keys(formResponses).includes(element.id);
    const isExpressionVariable =
      element?.properties?.autoSetValue && element?.properties?.workflowVariableId && !element?.properties?.isHidden && getCalculationValue;
    if (isExpressionVariable) {
      const evaluatedValue = getCalculationValue(element.properties.workflowVariableId);
      if (typeof evaluatedValue === "number") {
        return {
          ...element,
          value: evaluatedValue,
        };
      }
    }

    // If user provided a response for this element, update its value
    if (formResponses[element.id] !== undefined && formResponses[element.id] !== null) {
      // File-upload files (CMS + device) live in content.attachments; keep value empty like device-only submissions.
      if (element.type === "file-upload") {
        return {
          ...element,
          value: [],
        };
      }

      // For table elements, strip non-serializable rawFile and transient pendingDelete from fileUpload column cells
      if (element.type === "table" && Array.isArray(formResponses[element.id])) {
        const sanitizedRows = formResponses[element.id].map((row) => {
          const newRow = { ...row };
          element.properties?.columns?.forEach((col, colIndex) => {
            const key = `col_${colIndex}`;
            if (col.type === "fileUpload" && newRow[key] && typeof newRow[key] === "object") {
              const { rawFile, pendingDelete, ...rest } = newRow[key];
              newRow[key] = rest;
            }
          });
          return newRow;
        });
        return { ...element, value: sanitizedRows };
      }

      return {
        ...element,
        value: formResponses[element.id],
      };
    }

    // For hidden workflow variable elements linked to table calculations or expressions
    if (element.properties?.isHidden && element.properties?.workflowVariableId && getCalculationValue) {
      const calculatedValue = getCalculationValue(element.properties.workflowVariableId);
      if (typeof calculatedValue === "number") {
        return {
          ...element,
          value: calculatedValue,
        };
      }
    }

    // Otherwise, keep element as is (with existing value or empty)
    return {
      ...element,
      value: hasUserResponse ? formResponses[element.id] : element.value,
    };
  };

  /**
   * Create updated form content with user responses and default values
   * @param {Object} formData - Original form data
   * @param {Object} formResponses - User form responses
   * @param {Function} getCalculationValue - Function to get calculated values
   * @returns {Object} Updated form content
   */
  const createUpdatedFormContent = (formData, formResponses, getCalculationValue = null, formTab = FORM_TABS.SUBSECTIONS_OBJ) => {
    return {
      ...formData?.content,
      [formTab?.hostSection]: formData?.content?.[formTab?.hostSection]?.map((subsection) => ({
        ...subsection,
        elements: subsection?.elements?.map((element) => {
          // Handle grid containers
          if (element.type === "row-container" && element.gridElements) {
            const updatedGridElements = element.gridElements.map((row) =>
              row.map((gridElement) => {
                if (gridElement) {
                  const hasUserResponse = Object.keys(formResponses).includes(gridElement.id);
                  const updatedGridElement = updateElementWithResponse(gridElement, formResponses, getCalculationValue);
                  // Apply default values if no user response
                  return applyDefaultValues(updatedGridElement, hasUserResponse);
                }
                return gridElement; // Keep null elements as null
              })
            );

            return {
              ...element,
              gridElements: updatedGridElements,
            };
          }

          // Handle regular elements
          const hasUserResponse = Object.keys(formResponses).includes(element.id);
          const updatedElement = updateElementWithResponse(element, formResponses, getCalculationValue);
          // Apply default values if no user response
          return applyDefaultValues(updatedElement, hasUserResponse);
        }),
      })),
    };
  };

  const completeAndForwardTask = async (taskHistoryId, taskId, IsRuleBasedTask) => {
    const payload = {
      taskHistoryId: taskHistoryId,
      taskId: taskId,
      userId: user.id,
      positionId: positionId,
    };
    const response =  IsRuleBasedTask ? await completeAndForwardRuleBased(payload) : await completeAndForward(payload);
    dispatch(setShowTaskSidebar({ showTaskSidebar: false }));
    return response;
  };

  /**
   * Collect IDs of CMS files marked for deletion after successful form submission.
   * These are cells where the user selected a file with "Auto Remove after upload" checked.
   * @param {Object} formResponses - User form responses
   * @param {Object} formContent - Form structure
   * @returns {string[]} Array of CMS attachment IDs to delete
   */
  const extractPendingDeleteIds = (formResponses, formContent) => {
    const ids = [];
    formContent?.[formTab?.hostSection]?.forEach((subsection) => {
      subsection.elements?.forEach((element) => {
        // file-upload elements (standalone and inside grid containers)
        const scanFileUploadElement = (el) => {
          if (el.type === "file-upload" && formResponses[el.id]) {
            const elementFiles = formResponses[el.id];
            if (Array.isArray(elementFiles)) {
              elementFiles.forEach((file) => {
                if (file?.source === "cms" && file?.pendingDelete && file?.id) {
                  ids.push(file.id);
                }
              });
            }
          }
        };

        scanFileUploadElement(element);

        if (element.type === "row-container" && element.gridElements) {
          element.gridElements.forEach((row) => {
            row.forEach((gridElement) => {
              if (gridElement) scanFileUploadElement(gridElement);
            });
          });
        }

        // table elements with fileUpload columns
        if (element.type === "table" && formResponses[element.id]) {
          const tableRows = formResponses[element.id];
          element.properties?.columns?.forEach((col, colIndex) => {
            if (col.type === "fileUpload") {
              tableRows.forEach((row) => {
                const cell = row[`col_${colIndex}`];
                if (cell?.source === "cms" && cell?.pendingDelete && cell?.id) {
                  ids.push(cell.id);
                }
              });
            }
          });
        }
      });
    });
    return ids;
  };

  const getAttachmentId = (row) => row?.attachmentId ?? row?.AttachmentId ?? row?.AttachmentID;

  /** True when the user file row is the same attachment as the stored content.attachments entry. */
  const formFileEntryStillReferencesAttachment = (file, attachmentId) => {
    if (!file || typeof file !== "object" || attachmentId == null || attachmentId === "") return false;
    const fileAttachmentId = getAttachmentId(file);
    return fileAttachmentId != null && String(fileAttachmentId) === String(attachmentId);
  };

  /**
   * Drops task attachment rows under content.attachments that no longer match formResponses
   * (same rules as extractRemovedTaskAttachmentPayloads) so saved content and FileUploadRenderer stay in sync.
   */
  const pruneAttachmentsMapWithFormResponses = (attachmentsMap, formResponses, formContent, formTab) => {
    if (!attachmentsMap || typeof attachmentsMap !== "object") return attachmentsMap;
    const next = { ...attachmentsMap };

    const pruneSlot = (slotKey, responseValue) => {
      // Only sync slots the user has in formResponses (device files are seeded there on edit open).
      if (!(slotKey in formResponses)) return;
      const prevList = attachmentsMap[slotKey];
      if (!prevList) return;
      const list = Array.isArray(prevList) ? prevList : [prevList];
      const files = Array.isArray(responseValue) ? responseValue : responseValue ? [responseValue] : [];
      const kept = list.filter((rec) => {
        const attachmentId = getAttachmentId(rec);
        return files.some((f) => formFileEntryStillReferencesAttachment(f, attachmentId));
      });
      if (kept.length === 0) {
        delete next[slotKey];
      } else if (Array.isArray(prevList)) {
        next[slotKey] = kept;
      } else {
        next[slotKey] = kept[0];
      }
    };

    if (!formContent?.[formTab?.hostSection]) return next;

    const elementPruneHandlers = {
      "file-upload": (el) => pruneSlot(el.id, formResponses[el.id]),
      "row-container": (el) => {
        el.gridElements?.forEach((row) => {
          row.forEach((gridEl) => gridEl && pruneElementForAttachments(gridEl));
        });
      },
      table: (el) => {
        const re = new RegExp(`^${el.id}__(\\d+)__(\\d+)$`);
        Object.keys(attachmentsMap).forEach((akey) => {
          const m = akey.match(re);
          if (!m) return;
          const rowIndex = Number(m[1]);
          const colIndex = Number(m[2]);
          const col = el.properties?.columns?.[colIndex];
          if (col?.type !== "fileUpload") return;
          const cell = formResponses[el.id]?.[rowIndex]?.[`col_${colIndex}`];
          const hasCellFile = cell && typeof cell === "object" && (cell.name || cell.filename);
          pruneSlot(akey, hasCellFile ? [cell] : []);
        });
      },
    };

    const pruneElementForAttachments = (element) => {
      elementPruneHandlers[element?.type]?.(element);
    };

    formContent[formTab.hostSection].forEach((subsection) => {
      subsection.elements?.forEach((element) => pruneElementForAttachments(element));
    });

    return next;
  };

  /**
   * Main form submission handler with file upload support
   * @param {Object} params - Submission parameters
   * @param {Object} params.formData - The form data structure
   * @param {Object} params.formResponses - User form responses
   * @param {string} params.companyId - Company ID
   * @param {string} params.taskId - Task ID (optional, for existing tasks)
   * @param {Function} params.getAttachmentObject - Function to create attachment objects
   * @param {Function} params.onSuccess - Success callback
   * @param {Function} params.onError - Error callback
   * @returns {Promise<Object>} The submission response
   */
  const submitFormWithFiles = async ({
    formData,
    formResponses,
    taskId,
    getAttachmentObject,
    onSuccess,
    onError,
    taskHistoryId,
    getCalculationValue,
    submissionType,
    formTab = FORM_TABS.SUBSECTIONS_OBJ,
    IsRuleBasedTask = false,
  }) => {
    try {
      // Collect CMS pending-delete IDs BEFORE content sanitization strips pendingDelete flags
      const pendingDeleteIds = extractPendingDeleteIds(formResponses, formData?.content);
      const attachmentsMap = formData?.content?.attachments;

      // Extract files from form responses before processing
      const { files, filesWithIds } = extractFilesFromFormResponses(formResponses, formData?.content, formTab);
      // Collect CMS file links (attachmentKeys) to send as newUploadedFiles
      const cmsFileLinks = extractCmsFileLinks(formResponses, formData?.content, formTab);
      // Create updated form content
      const updatedFormContentBase = createUpdatedFormContent(formData, formResponses, getCalculationValue, formTab);
      const updatedFormContent =
        attachmentsMap && typeof attachmentsMap === "object"
          ? {
              ...updatedFormContentBase,
              attachments: pruneAttachmentsMapWithFormResponses(attachmentsMap, formResponses, formData?.content, formTab),
            }
          : updatedFormContentBase;
      // Create FormData for multipart upload
      const formDataPayload = new FormData();
      // Append basic data as JSON strings
      formDataPayload.append("formTab", formTab?.type || FORM_TABS.SUBSECTIONS_OBJ.type);
      formDataPayload.append("formId", formData._id || formData.id);
      formDataPayload.append("companyId", user?.companyId);
      formDataPayload.append(
        "content",
        JSON.stringify({
          ...updatedFormContent,
        })
      );
      formDataPayload.append("submissionId", formData._id || formData.id);
      formDataPayload.append("taskId", taskId);
      formDataPayload.append("uploadedCMSFile", JSON.stringify(cmsFileLinks));
      // Append files array
      filesWithIds.forEach((file, index) => {
        formDataPayload.append(`${file.id}`, file.file);
      });
      // Submit form with multipart data
      const response = await dispatch(submissionType === "update" ? editFormSubmission(formDataPayload) : submitForm(formDataPayload));

      // Call success callback
      if (onSuccess && response) {
        onSuccess(response);
        if (taskHistoryId && taskId && submissionType === "submit") completeAndForwardTask(taskHistoryId, taskId, IsRuleBasedTask);
      }

      if (response) {
        // Fire-and-forget: delete CMS files flagged for removal after successful submission
        if (pendingDeleteIds.length > 0) {
          deleteContentAttachment({ attachmentIds: pendingDeleteIds }).catch(() => {});
        }

        const currentPath = window.location.pathname;
        if (currentPath === "/home" || currentPath === "/") {
          window.dispatchEvent(new CustomEvent("landingPageRefresh"));
        }
        return response;
      } else {
        const errorMessage = response?.message || "Form submission failed";
        getNotification(errorMessage, notifyIcon.ERROR_ICON);
        if (onError) {
          onError(new Error(errorMessage));
        }
        return null;
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage = "Form submission failed";
      getNotification(errorMessage, notifyIcon.ERROR_ICON);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  };

  return {
    // File conversion utilities
    convertKendoFileToFile,
    convertBlobURLToFile,
    getBlobFromURL,
    blobToFile,

    // Form processing utilities
    extractFilesFromFormResponses,
    processFilesForUpload,
    createUpdatedFormContent,
    applyDefaultValues,

    // Upload utilities
    uploadTaskAttachments,

    // Main submission handler
    submitFormWithFiles,
  };
};

/**
 * Recursively extract all elements from form structure (shared for validation and initial responses).
 * Handles nested containers like grids and future hierarchical structures.
 * @param {Array} elements - Array of form elements
 * @returns {Array} Flattened array of all elements
 * @author Sarthak Arora
 */
/** Map rows from content.attachments into file-upload formResponses (CMS via attachmentKey, device via url/attachmentId). */
const storedAttachmentRowsToFormFiles = (storedRows) => {
  const list = storedRows == null ? [] : Array.isArray(storedRows) ? storedRows : [storedRows];
  return list.map((row) => {
    const filename = row?.filename ?? row?.AttachmentName ?? row?.name ?? "";
    if (row?.attachmentKey) {
      const attachmentId = row?.attachmentId ?? row?.AttachmentId;
      return {
        name: filename,
        filename,
        size: row?.size ?? row?.FileSize,
        attachmentKey: row.attachmentKey,
        id: row?.id ?? row?._id ?? row?.cmsId,
        dateCreated: row?.dateCreated ?? row?.createdAt,
        source: "cms",
        attachmentId,
        AttachmentId: attachmentId,
      };
    }
    const attachmentId = row?.attachmentId ?? row?.AttachmentId;
    return {
      name: filename,
      filename,
      size: row?.size ?? row?.FileSize,
      source: "device",
      attachmentId,
      AttachmentId: attachmentId,
    };
  });
};

const getAllElementsRecursively = (elements) => {
  if (!elements || !Array.isArray(elements)) return [];
  const allElements = [];
  elements.forEach((element) => {
    if (!element) return;
    allElements.push(element);
    if (element.type === "row-container" && element.gridElements) {
      const gridElements = element.gridElements.flat().filter(Boolean);
      allElements.push(...getAllElementsRecursively(gridElements));
    }
  });
  return allElements;
};

/**
 * Build initial formResponses from form content that already has saved values (e.g. when reopening to edit).
 * Used so validation and submit see the same values the user sees from element.value.
 * @param {Object} formContent - Form content (formData.content or formData when it is the content root)
 * @returns {Object} Object keyed by element id with value from element.value or element.properties?.value
 * @author Sarthak Arora
 */
export const getInitialResponsesFromContent = (formContent, formTab = FORM_TABS.SUBSECTIONS_OBJ) => {
  const initial = {};
  const scResponses = {};
  if (!formContent?.[formTab?.hostSection] || !Array.isArray(formContent?.[formTab?.hostSection])) return initial;
  Object.keys(FORM_TABS).forEach((key) => {
    const tab = FORM_TABS[key];
    formContent?.[tab?.hostSection]?.forEach((subsection) => {
      if (!subsection.elements) return;
      const allElements = collectFormElements(subsection.elements);
      allElements.forEach((element) => {
        const smartChipId = element?.smartChipId || element?.properties?.workflowVariableId;
        if (smartChipId && element?.value) {
          scResponses[smartChipId] = element?.value;
        }
        const value = element?.value ?? element?.properties?.value;
        if (element.type === "file-upload") {
          const filesFromAttachments = storedAttachmentRowsToFormFiles(formContent?.attachments?.[element.id]);
          if (filesFromAttachments.length > 0) {
            initial[element.id] = filesFromAttachments;
          } else if (value !== undefined && value !== null) {
            // Legacy submissions that still store CMS metadata in element.value
            const legacyCms = (Array.isArray(value) ? value : [value]).filter((f) => f?.source === "cms");
            if (legacyCms.length > 0) initial[element.id] = legacyCms;
          }
        } else if (value !== undefined && value !== null) {
          initial[element.id] = value;
        }
      });
    });
  });
  return { initial, scResponses };
};

/**
 * Build initial responses map from all form sections (main + hidden subsections).
 * Used for read-only task form so MultiDimension display fields and other renderers get values by element id.
 */
export const getAllInitialResponsesFromContent = (formContent) => {
  const { initial: initialSubsections, scResponses: scResponsesSubsections } = getInitialResponsesFromContent(formContent, FORM_TABS.SUBSECTIONS_OBJ);
  const { initial: initialHiddenSubsections, scResponses: scResponsesHiddenSubsections } = getInitialResponsesFromContent(
    formContent,
    FORM_TABS.HIDDEN_SUBSECTIONS_OBJ
  );
  return {
    initial: { ...initialSubsections, ...initialHiddenSubsections },
    scResponses: { ...scResponsesSubsections, ...scResponsesHiddenSubsections },
  };
};

/**
 * Custom hook for handling form field validation
 * Provides utilities for checking required fields and validation status
 * @param {Object} formData - The form data structure
 * @param {Object} formResponses - User form responses
 * @param {Object} smartChipResponses - Smart chip responses
 * @returns {Object} Object containing validation functions and status
 */
export const useFormValidation = (formData, formResponses, formTab = FORM_TABS.SUBSECTIONS_OBJ) => {
  /**
   * Get all required field IDs from form data
   * @returns {string[]} Array of required field IDs
   */
  const getRequiredFieldIds = (smartChipResponses) => {
    const formContent = formData?.content || formData;
    if (!formContent?.[formTab?.hostSection]) return [];

    const requiredIds = [];

    formContent?.[formTab?.hostSection]?.forEach((subsection) => {
      if (!isShowIfVisible(subsection, smartChipResponses)) return;
      if (subsection.elements) {
        const allElements = collectFormElements(subsection.elements);

        allElements.forEach((element) => {
          if (!isShowIfVisible(element, smartChipResponses)) return;
          if (element.properties?.isHidden === true) return; // Skip hidden elements
          if (element.properties?.required === true) {
            requiredIds.push(element.id);
          }
        });
      }
    });

    return requiredIds;
  };

  /**
   * Check if all required fields are filled
   * @returns {boolean} True if all required fields have values
   */
  const areAllRequiredFieldsFilled = (smartChipResponses) => {
    const requiredFieldIds = getRequiredFieldIds(smartChipResponses);
    const formContent = formData?.content || formData;

    if (requiredFieldIds.length === 0) return true; // No required fields

    return requiredFieldIds.every((fieldId) => {
      const value = formResponses[fieldId];

      // Check if user has provided a value
      const hasUserValue = !(
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "string" && value.trim() === "")
      );

      if (hasUserValue) return true;

      // If no user value, check if field has a defaultValue
      let hasDefaultValue = false;
      formContent?.[formTab?.hostSection]?.forEach((subsection) => {
        if (subsection.elements) {
          const allElements = collectFormElements(subsection.elements);
          allElements.forEach((element) => {
            if (element.id === fieldId && (element.properties?.defaultValue || element.properties?.default)) {
              const defaultVal = element.properties.defaultValue || element.properties.default;
              // Check if defaultValue is not empty
              hasDefaultValue = !(
                defaultVal === null ||
                defaultVal === undefined ||
                defaultVal === "" ||
                (Array.isArray(defaultVal) && defaultVal.length === 0) ||
                (typeof defaultVal === "string" && defaultVal.trim() === "")
              );
            }
          });
        }
      });

      return hasDefaultValue;
    });
  };

  /**
   * Get missing required field labels for user feedback
   * @returns {string[]} Array of missing required field labels
   */
  const getMissingRequiredFields = (smartChipResponses = {}) => {
    const requiredFieldIds = getRequiredFieldIds(smartChipResponses);
    const missingFields = [];
    const formContent = formData?.content || formData;

    // Helper function to check if a field is missing
    const isFieldMissing = (element) => {
      const value = formResponses[element.id];

      // Check if user has provided a value
      const hasUserValue = !(
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "string" && value.trim() === "")
      );

      if (hasUserValue) return false; // Field is filled by user

      // Check if field has a defaultValue
      const defaultVal = element.properties?.defaultValue;
      const hasDefaultValue = !(
        defaultVal === null ||
        defaultVal === undefined ||
        defaultVal === "" ||
        (Array.isArray(defaultVal) && defaultVal.length === 0) ||
        (typeof defaultVal === "string" && defaultVal.trim() === "")
      );

      // Field is missing if no user value AND no default value
      return !hasDefaultValue;
    };

    formContent?.[formTab?.hostSection]?.forEach((subsection) => {
      if (!isShowIfVisible(subsection, smartChipResponses)) return;
      if (subsection.elements) {
        const allElements = collectFormElements(subsection.elements);

        allElements.forEach((element) => {
          if (!isShowIfVisible(element, smartChipResponses)) return;
          if (element.properties?.required === true && requiredFieldIds.includes(element.id)) {
            if (isFieldMissing(element)) {
              missingFields.push(element.properties?.label || `Field ${element.id}`);
            }
          }
        });
      }
    });

    return missingFields;
  };

  return {
    getRequiredFieldIds,
    areAllRequiredFieldsFilled,
    getMissingRequiredFields,
  };
};
